import type { Vault } from '@/types/vault'
import { getDefiSdk } from '@/utils/defiSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS, SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { useMemo } from 'react'
import { type Address, formatUnits } from 'viem'

export const vaultHolderEventsQueryKey = ['vaultHolderEvents'] as const

interface VaultHolderEventsQueryParams {
  address: Address | undefined
  vault: Vault
}

type VaultEvent = {
  timestamp: number
  blockNumber: string
  eventType: 'deposit' | 'withdrawal'
  assetAmountNative: string
  sharePrice: number
  lpTokenAmount: string
  transactionHash: string
  logIndex: number
}

function vaultHolderEventsQueryOptions({ address, vault }: VaultHolderEventsQueryParams) {
  return queryOptions({
    queryKey: [
      ...vaultHolderEventsQueryKey,
      {
        network: vault.network.name,
        vaultId: vault.vaultId,
        userAddress: address,
      },
    ] as const,
    async queryFn({ queryKey }) {
      const { network, vaultId, userAddress } = queryKey[1]
      if (!userAddress) return null
      const defiSdk = getDefiSdk()

      const childrenResults = await Promise.all(
        (vault?.childrenVaults ?? []).map((childrenVault) =>
          defiSdk.getUserVaultEvents({
            path: {
              network,
              vaultId: childrenVault.vaultId,
              userAddress,
            },
          })
        )
      )

      const result = await defiSdk.getUserVaultEvents({
        path: { network, vaultId, userAddress },
      })

      const allResults = [...childrenResults, result]
      const eventsList: VaultEvent[] = allResults.flatMap((r) => r.data)
      const lpTokenDecimals = result.lpToken.decimals

      return { events: eventsList, lpTokenDecimals }
    },
    select(data) {
      if (data == null) {
        return null
      }

      const { events, lpTokenDecimals } = data
      const assetDecimals = vault.asset.decimals
      const sortedEvents = [...events].sort((a, b) => a.timestamp - b.timestamp)

      let cumulativeLpTokens = 0n

      return sortedEvents.map((event) => {
        const lpTokenAmount = BigInt(event.lpTokenAmount)

        if (event.eventType === 'deposit') {
          cumulativeLpTokens += lpTokenAmount
        } else {
          cumulativeLpTokens -= lpTokenAmount
        }

        const sharePriceBigInt = BigInt(Math.floor(event.sharePrice * 1e18))

        const decimalAdjustment = assetDecimals - lpTokenDecimals
        const positionValueInAsset =
          decimalAdjustment >= 0
            ? (cumulativeLpTokens * sharePriceBigInt * BigInt(10 ** decimalAdjustment)) / BigInt(1e18)
            : (cumulativeLpTokens * sharePriceBigInt) / BigInt(1e18) / BigInt(10 ** -decimalAdjustment)

        return {
          timestamp: event.timestamp,
          blockNumber: event.blockNumber,
          eventType: event.eventType,
          transactionHash: event.transactionHash,
          logIndex: event.logIndex,
          eventAssetAmount: {
            native: event.assetAmountNative,
          },
          resultingPosition: {
            native: positionValueInAsset.toString(),
          },
        }
      })
    },
    enabled: !!address,
    retry: 2,
    staleTime: 5 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: false,
  })
}

export function useVaultHolderEventsQuery({ address, vault }: VaultHolderEventsQueryParams) {
  const { data: events, isLoading } = useQuery(vaultHolderEventsQueryOptions({ address, vault }))

  const historicalChartData = useMemo(() => {
    if (!events) return []

    return events.map((event) => ({
      timestamp: event.timestamp * SECOND_IN_MILLISECONDS,
      value: Number(formatUnits(BigInt(event.resultingPosition.native), vault.asset.decimals)),
    }))
  }, [events, vault.asset.decimals])

  const reversedEvents = useMemo(() => {
    if (!events) return []
    return [...events].reverse()
  }, [events])

  return { events: reversedEvents, historicalChartData, isLoading }
}
